import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import api, { errMsg, saveAuth } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Consumes a magic-link token from `?token=…` and either logs the user in
 * (redirect to guest dashboard) or shows a friendly error with a "Richiedi
 * nuovo link" CTA back to `/guest/auth`.
 */
export default function GuestMagicConsume() {
  const nav = useNavigate();
  const location = useLocation();
  const { lang } = useLang();
  const IT = lang === "it";
  const processed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    if (!token) {
      setError(IT ? "Link non valido. Il token è mancante." : "Invalid link. Missing token.");
      return;
    }
    (async () => {
      try {
        const r = await api.post("/guest/auth/magic-link/consume", { token });
        saveAuth(r.data.token, r.data.user);
        toast.success(IT ? "Accesso effettuato!" : "You're in!");
        nav("/guest/dashboard", { replace: true });
      } catch (e) {
        setError(errMsg(e, IT ? "Link non valido o scaduto" : "Invalid or expired link"));
      }
    })();
  }, [location.search, nav, IT]);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-background px-6" data-testid="magic-consume-error">
        <div className="max-w-md w-full text-center border border-destructive/30 rounded-lg bg-destructive/5 p-8">
          <AlertCircle className="w-8 h-8 text-destructive mx-auto mb-3"/>
          <h2 className="font-display text-2xl mb-2">{IT ? "Link non utilizzabile" : "Link not usable"}</h2>
          <p className="text-sm text-muted-foreground mb-4">{error}</p>
          <Button asChild size="sm" className="lift" data-testid="magic-request-new">
            <Link to="/guest/auth?mode=login">{IT ? "Richiedi nuovo link" : "Request a new link"}</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-background" data-testid="magic-consume">
      <div className="text-center">
        <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin mx-auto mb-4"/>
        <p className="text-sm text-muted-foreground">{IT ? "Accesso in corso…" : "Signing you in…"}</p>
      </div>
    </div>
  );
}
